


import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef, Component, inject, PLATFORM_ID, ViewChild
} from '@angular/core';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { AuthService } from '@services/auth.service';
import { Person, PersonsService } from '@services/persons.service';

import { DrawerContentComponent } from './components/drawer-content/drawer-content.component';
import { DrawerSidebarComponent } from './components/drawer-sidebar/drawer-sidebar.component';
import { TranslitPipe } from './pipes/translit.pipe';
import { PushNotificationsService } from './services/push-notifications.service';
import { PwaUpdateService } from './services/pwa-update.service';
import { ResponsiveService } from './services/responsive.service';
import { TagsService } from './services/tags.service';
import { ThemeService } from './services/theme.service';
import { UtilsService } from './services/utils.service';
import { WebPushService } from './services/web-push.service';

@Component({
  selector: 'app-root',
  imports: [
    MatSidenavModule,
    DrawerContentComponent,
    TranslitPipe,
    DrawerSidebarComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  @ViewChild('drawer') drawer!: MatDrawer;

  private intervalId: any;
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private authService = inject(AuthService);
  private responsiveService = inject(ResponsiveService);
  private pwaUpdateService = inject(PwaUpdateService);
  private pushNotificationsService = inject(PushNotificationsService);
  private webPushService = inject(WebPushService);
  private tagsService = inject(TagsService);
  private personsService = inject(PersonsService);
  protected utilsService = inject(UtilsService); // init injector in service

  public themeService = inject(ThemeService);
  public changeDetectorRef = inject(ChangeDetectorRef);
  public userProfileChecked = false;
  public response!: Person[];
  public translitTriggered = false;

  public over1200 = false;

  public currentCity = '';

  async ngOnInit() {
    // The server renders the shell only: no intervals (they would delay
    // stability), no full person preload (5000+ reads per request). The
    // splash-screen gate is a browser concern, so open it immediately.
    if (!this.isBrowser) {
      this.userProfileChecked = true;
      this.translitTriggered = true;
      this.response = [];
      return;
    }

    this.initAuth();
    this.runCityMixer();
    this.listenTranslit();
    this.initPWA();
    this.initPush();
    this.initListeners();
    await this.initPersonList();
  }

  ngAfterViewInit() {
    if (!this.responsiveService.queryUnder1200) {
      return;
    }
    this.over1200 = this.responsiveService.over1200;
    this.changeDetectorRef.detectChanges();

    this.responsiveService.queryUnder1200.addListener(() => {
      this.over1200 = this.responsiveService.over1200;
      this.changeDetectorRef.detectChanges();
    });
  }

  runCityMixer(): void {
    const cityList = this.tagsService.cityList;

    UtilsService.shuffleListOrder(cityList);
    this.currentCity = cityList[0].name;
    let currentIndex = 3;

    this.intervalId = setInterval(() => {
      this.currentCity = cityList[currentIndex].name;
      currentIndex++;

      this.changeDetectorRef.detectChanges();
    }, 75);
  }

  listenTranslit() {
    this.themeService.translitTrigger$$.subscribe({
      next: (_ => {
        this.translitTriggered = false;
        setTimeout(() => {
          this.translitTriggered = true;
          this.changeDetectorRef.detectChanges();
        }, 400);
      })
    });
  }

  async initPersonList() {
    this.response = await this.personsService.getItemsServer();

    setTimeout(() => {
      this.changeDetectorRef.detectChanges();

      if (this.intervalId) {
        clearInterval(this.intervalId);
      }

      if (this.drawer) {
        this.responsiveService.setDrawer(this.drawer);
      }
    }, 100);
  }


  initPWA() {
    this.pwaUpdateService.listenPwaUpdates();
  }

  initPush() {
    this.pushNotificationsService.init();
    this.webPushService.init();
  }

  initListeners() {
    this.responsiveService.initResponsiveListener();
  }

  initAuth() {
    this.authService.initAuth();
    this.authService.user$.subscribe((_) => {
      this.userProfileChecked = true;
      setTimeout(() => {
        this.changeDetectorRef.detectChanges();
        if (this.drawer) {
          this.responsiveService.setDrawer(this.drawer);
        }
      }, 100);
    });
  }

}
